import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MisReferenceDto } from '../types';
import { misName, qty, unitWord } from '../utils/picklistProgress';
import { CountChip, CountChipRow } from './CountChip';

interface MisChipProps {
  refs: readonly MisReferenceDto[];
  /** The item's unit, for each slip's quantity in the card. */
  unit: string;
  className?: string;
}

const LINK = 'inline-flex min-w-0 items-center gap-1 font-mono text-[11px] text-info underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The slip(s) something was issued on (owner, 2026-10-02). One slip is its number, a link that opens it in NetSuite.
 * Two or more are ONE count chip, "N MIS", whose card lists each slip as a link with what went out on it — the column
 * stays one chip wide however many partial posts there were. Nothing renders for no slips: the caller owns its dash.
 */
export function MisChip({ refs, unit, className }: MisChipProps) {
  if (refs.length === 0) return null;

  if (refs.length === 1) {
    const only = refs[0];
    return (
      <a
        href={only.url}
        target="_blank"
        rel="noopener noreferrer"
        // The Items row is clickable: following the link must not also toggle it.
        onClick={(e) => e.stopPropagation()}
        title={`${misName(only)} — opens in NetSuite`}
        className={cn(LINK, 'max-w-full', className)}
      >
        <span className="truncate">{misName(only)}</span>
      </a>
    );
  }

  return (
    <CountChip
      label={`${refs.length} MIS`}
      ariaLabel={`${refs.length} MIS slips — show the list`}
      heading={`Issued on ${refs.length} slips`}
      className={className}
    >
      {refs.map((ref) => (
        <CountChipRow key={ref.misId}>
          <a href={ref.url} target="_blank" rel="noopener noreferrer" className={LINK}>
            {misName(ref)}
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
          </a>
          {ref.quantity != null && (
            <span className="whitespace-nowrap tabular-nums text-muted-foreground">
              {qty(ref.quantity)} {unitWord(unit, ref.quantity)}
            </span>
          )}
        </CountChipRow>
      ))}
    </CountChip>
  );
}
